import { Injectable, inject } from '@angular/core';
import {
  ActionCodeInfo,
  applyActionCode,
  checkActionCode,
  confirmPasswordReset,
  sendPasswordResetEmail,
} from 'firebase/auth';
import type { User as FirebaseUser } from 'firebase/auth';
import { FirebaseInjectionHelperService } from './firebase-injection-helper.service';

/**
 * Thin wrapper over the Firebase Auth action-code API used by the
 * `/auth/action` handler (#835).
 *
 * Boundary: talks only to Firebase Auth. Exists so the handler can be tested:
 * the Firebase SDK is an ES module, so its functions cannot be spied on
 * directly, but this service's instance methods can.
 */
@Injectable({ providedIn: 'root' })
export class AuthActionCodeService {
  private readonly firebaseHelper = inject(FirebaseInjectionHelperService);

  /**
   * Reads what an action code is for without consuming it.
   * @param code - The `oobCode` from the email link.
   * @returns The operation and the email addresses it concerns.
   */
  checkActionCode(code: string): Promise<ActionCodeInfo> {
    return checkActionCode(this.firebaseHelper.getAuth(), code);
  }

  /**
   * Applies (consumes) a verify-email, recover-email or change-email code.
   * @param code - The `oobCode` from the email link.
   */
  applyActionCode(code: string): Promise<void> {
    return applyActionCode(this.firebaseHelper.getAuth(), code);
  }

  /**
   * Consumes a password-reset code and sets the new password.
   * @param code - The `oobCode` from the email link.
   * @param newPassword - The new password.
   */
  confirmPasswordReset(code: string, newPassword: string): Promise<void> {
    return confirmPasswordReset(this.firebaseHelper.getAuth(), code, newPassword);
  }

  /**
   * Sends a password-reset email (offered after an email recovery).
   * @param email - The account's email address.
   */
  sendPasswordResetEmail(email: string): Promise<void> {
    return sendPasswordResetEmail(this.firebaseHelper.getAuth(), email);
  }

  /**
   * The user signed in on this browser, if any.
   * @returns The Firebase user, or null.
   */
  currentUser(): FirebaseUser | null {
    return this.firebaseHelper.getAuth().currentUser;
  }
}
